import type { Session } from "@supabase/supabase-js";

type LockRequest = LockManager["request"];
type AnyLockGrantedCallback = LockGrantedCallback<unknown>;

const SUPABASE_LOCK_PREFIX = "lock:sb-";
const AUTH_STORAGE_SUFFIX = "-auth-token";
const LOCK_PATCH_FLAG = "__casa87SupabaseLockPatch";

type PatchedNavigator = Navigator & {
  [LOCK_PATCH_FLAG]?: boolean;
};

export const setupSupabaseAuthStability = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") return;

  const patchedNavigator = navigator as PatchedNavigator;
  if (patchedNavigator[LOCK_PATCH_FLAG]) return;

  const locks = navigator.locks;
  if (!locks?.request) return;

  const originalRequest = locks.request.bind(locks) as LockRequest;
  const stableRequest = async (name: string, optionsOrCallback: LockOptions | AnyLockGrantedCallback, maybeCallback?: AnyLockGrantedCallback) => {
    const callback = typeof optionsOrCallback === "function" ? optionsOrCallback : maybeCallback;

    if (name.startsWith(SUPABASE_LOCK_PREFIX) && callback) {
      return callback({ name, mode: "exclusive" } as Lock);
    }

    return typeof optionsOrCallback === "function"
      ? originalRequest(name, optionsOrCallback)
      : originalRequest(name, optionsOrCallback, maybeCallback as AnyLockGrantedCallback);
  };

  try {
    Object.defineProperty(locks, "request", {
      configurable: true,
      writable: true,
      value: stableRequest,
    });
    if (locks.request !== stableRequest) {
      locks.request = stableRequest as LockRequest;
    }
    patchedNavigator[LOCK_PATCH_FLAG] = true;
  } catch (error) {
    try {
      locks.request = stableRequest as LockRequest;
      patchedNavigator[LOCK_PATCH_FLAG] = true;
    } catch {
      console.warn("[Auth] Não foi possível aplicar estabilidade de sessão", error);
    }
  }
};

export const withTimeout = <T,>(promise: PromiseLike<T>, ms = 10000, message = "Tempo esgotado ao conectar. Tente novamente."): Promise<T> =>
  Promise.race([
    Promise.resolve(promise),
    new Promise<never>((_, reject) => {
      window.setTimeout(() => reject(new Error(message)), ms);
    }),
  ]);

export const getStoredSupabaseSession = (): Session | null => {
  if (typeof window === "undefined") return null;

  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key?.startsWith("sb-") || !key.endsWith(AUTH_STORAGE_SUFFIX)) continue;

      const raw = localStorage.getItem(key);
      if (!raw) continue;

      const parsed = JSON.parse(raw) as Partial<Session> & { currentSession?: Session; session?: Session };
      const session = parsed.currentSession ?? parsed.session ?? parsed;
      const expiresAt = typeof session.expires_at === "number" ? session.expires_at : 0;

      if (session.access_token && session.refresh_token && session.user && expiresAt > Date.now() / 1000) {
        return session as Session;
      }
    }
  } catch (error) {
    console.warn("[Auth] Não foi possível ler sessão local", error);
  }

  return null;
};

/**
 * Derive the Supabase storage key from the project URL.
 * Matches the default used by @supabase/supabase-js: `sb-<ref>-auth-token`.
 */
export const getSupabaseStorageKey = (): string | null => {
  const url = (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_SUPABASE_URL;
  if (!url) return null;
  try {
    const host = new URL(url).hostname;
    const ref = host.split(".")[0];
    return `sb-${ref}${AUTH_STORAGE_SUFFIX}`;
  } catch {
    return null;
  }
};

/**
 * Persist a session to localStorage in the format the Supabase client expects,
 * so that on next page load `getSession()` and our `getStoredSupabaseSession()`
 * both pick it up without going through any cross-tab Web Lock.
 */
export const persistSupabaseSession = (session: Session | null): void => {
  if (typeof window === "undefined") return;
  const key = getSupabaseStorageKey();
  if (!key) return;
  try {
    if (session) {
      localStorage.setItem(key, JSON.stringify(session));
    } else {
      localStorage.removeItem(key);
    }
  } catch (err) {
    console.warn("[Auth] Não foi possível gravar sessão local", err);
  }
};